import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, Inject, Input } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CharityCategory } from '../Models/CharityCategory';
import { User } from '../Models/User';
import { forkJoin } from 'rxjs';

interface RegionVm {
  id: number;
  code: string;
  name: string;
  subscriptionItemId?: number;
  isTemp?: boolean;
  removedPending?: boolean;
}
interface SelectedDiscretionaryCategory {
  id: number;
  category: string;
  activity: string;
  subscriptionItemId?: number;
  isTemp?: boolean;
  removedPending?: boolean;
}

@Component({
  selector: 'app-admin-subscription-item-dialog',
  templateUrl: './admin-subscription-item-dialog.component.html',
  styleUrls: ['./admin-subscription-item-dialog.component.css'],
  standalone: false
})
export class AdminSubscriptionItemDialogComponent {
  constructor(
    private http: HttpClient,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<AdminSubscriptionItemDialogComponent>
  ) {
    this.serviceTypeId = data.serviceTypeId;
    this.subscriptionId = data.subscriptionId;
  }

  ngOnInit() {
    // Support both Discretionary (2) and Trust (3) for category subscription flow.
    // Trusts (3) only need categories (no regions).
    if (this.serviceTypeId === 2 || this.serviceTypeId === 3) {
      // always fetch category lists
      this.getCharityCategoryList();
      this.fetchCharityCategories();

      // only load regions for discretionary service type
      if (this.serviceTypeId === 2) {
        this.getRegions();
      }
    }

    this.loggedInUserTag = `${this.loggedInUser?.lastName}, ${this.loggedInUser?.firstName}`;

    if (this.data && this.data.openMode === 'category') {
      this.openModeOnInit = 'category';
    } else if (this.data && this.data.openMode === 'region') {
      this.openModeOnInit = 'region';
    }

    this.prepopulateFromParentItems();
  }

  @Input({ required: true }) loggedInUser?: User;
  private loggedInUserTag = '';

  serviceTypeId: number;
  subscriptionId: number;

  beneficiarySearchQuery = {
    beneficiaryName: '',
    beneficiaryAliasName: '',
    beneficiaryURN: '',
    address: '',
    postcode: ''
  };

  beneficiarySearchResults: any[] = [];
  selectedBeneficiary: any = null;

  showAddBeneficiaryForm = false;
  newBeneficiary: any = {};
  alert = { text: '', type: '' };

  tooltipVisible: Record<number, boolean> = {};
  tooltipContent: Record<number, { aliases: string[]; previousNames: string[] }> = {};
  tooltipPosition: Record<number, { top: number; left: number }> = {};
  private tooltipHideTimeouts: { [key: number]: any } = {};
  private isMouseOverTooltip: { [key: number]: boolean } = {};

  hoveredRow: number | null = null;

  selectedBeneficiaryIds: Set<number> = new Set<number>();
  // Beneficiary ids that were already persisted on this subscription when the dialog opened.
  persistedBeneficiaryIds: Set<number> = new Set<number>();

  showOnlySelectedBeneficiaries = false;

  allCharityCategories: CharityCategory[] = [];
  selectedCharityCategory: any = null;

  regions: RegionVm[] = [];
  selectedRegion: RegionVm[] | null = [];

  isDiscretionaryCategoryPopupOpen = false;
  isDiscretionaryRegionPopupOpen = false;
  isBeneficiaryPopupOpen = false;

  selectedCategory = '';
  isCategoryDropdownOpen = false;
  filteredSubActivities: CharityCategory[] = [];
  selectedSubActivities: SelectedDiscretionaryCategory[] = [];

  isRegionDropdownOpen = false;
  filteredRegions: RegionVm[] = [];
  selectedRegions: RegionVm[] = [];

  activitySearchTerm = '';
  filteredActivitiesList: CharityCategory[] = [];

  regionSearchTerm = '';
  filteredRegionList: RegionVm[] = [];
  isSaving = false;

  temporarySelectedActivityIds: Set<number> = new Set<number>();
  temporarySelectedRegionIds: Set<number> = new Set<number>();

  private openModeOnInit: 'category' | 'region' | null = null;

  showOnlySelectedActivities = false;
  showOnlySelectedRegions = false;

  // Resolve the numeric beneficiary id for a row from either data source:
  // existing items carry `beneficiaryId` (real Beneficiary.Id); search results carry it as `id`.
  private beneficiaryKey(r: any): number | undefined {
    const k = r?.beneficiaryId ?? r?.id;
    return k == null ? undefined : Number(k);
  }

  toggleBeneficiary(result: any) {
    // Toggle selection by numeric beneficiary id. Ignore items without an id.
    const id = this.beneficiaryKey(result);
    if (id === undefined || Number.isNaN(id)) return;

    if (this.selectedBeneficiaryIds.has(id)) {
      this.selectedBeneficiaryIds.delete(id);
    } else {
      this.selectedBeneficiaryIds.add(id);
    }
  }

  trackById = (_: number, item: { id: number }) => item.id;

  isSelected(r: any): boolean {
    const id = this.beneficiaryKey(r);
    if (id === undefined || Number.isNaN(id)) return false;
    return this.selectedBeneficiaryIds.has(id);
  }

  get allSelected(): boolean {
    const list = this.beneficiarySearchResults ?? [];
    return list.length > 0 && list.every(r => this.isSelected(r));
  }

  get someSelected(): boolean {
    const list = this.beneficiarySearchResults ?? [];
    const any = list.some(r => this.isSelected(r));
    const all = list.length > 0 && list.every(r => this.isSelected(r));
    return any && !all;
  }

  selectAllVisible(): void {
    (this.beneficiarySearchResults ?? []).forEach(r => {
      const id = this.beneficiaryKey(r);
      if (id !== undefined && !Number.isNaN(id)) this.selectedBeneficiaryIds.add(id);
    });
  }

  clearAllVisible(): void {
    (this.beneficiarySearchResults ?? []).forEach(r => {
      const id = this.beneficiaryKey(r);
      if (id !== undefined && !Number.isNaN(id)) this.selectedBeneficiaryIds.delete(id);
    });
  }

  clearAll(): void {
    this.selectedBeneficiaryIds.clear();
  }

  fetchCharityCategories() {
    this.http.get<CharityCategory[]>('/charitycentral/getcharitycategories')
      .subscribe({
        next: (categories) => {
          this.allCharityCategories = categories;
          this.filteredActivitiesList = [...this.allCharityCategories];
          this.prepopulateCategoriesFromData();
          if (this.openModeOnInit === 'category') {
            this.onDiscretionaryLookup();
            this.openModeOnInit = null;
          }
        },
        error: () => { this.allCharityCategories = []; }
      });
  }

  filterActivities() {
    let source: CharityCategory[] | any[] = [];
    if (this.showOnlySelectedActivities) {
      source = this.selectedSubActivities.map(s => {
        const cat = this.allCharityCategories.find(a => a.id === s.id);
        if (cat) return cat;
        return { id: s.id, parent: s.category, child: s.activity } as CharityCategory;
      });
    } else {
      source = this.allCharityCategories;
    }

    if (!this.activitySearchTerm.trim()) {
      this.filteredActivitiesList = [...source];
    } else {
      const searchLower = this.activitySearchTerm.toLowerCase();
      this.filteredActivitiesList = source.filter(a => `${a.parent} - ${a.child}`.toLowerCase().includes(searchLower));
    }
  }

  filterRegions() {
    let source: RegionVm[] = [];
    if (this.showOnlySelectedRegions) {
      source = this.selectedRegions.map(s => {
        const reg = this.regions.find(r => r.id === s.id);
        if (reg) return reg;
        return { id: s.id, code: s.code || '', name: s.name || '' } as RegionVm;
      });
    } else {
      source = this.regions;
    }

    if (!this.regionSearchTerm.trim()) {
      this.filteredRegionList = [...source];
    } else {
      const searchLower = this.regionSearchTerm.toLowerCase();
      this.filteredRegionList = source.filter(a => `${a.name}`.toLowerCase().includes(searchLower));
    }
  }

  isActivitySelected(activity: CharityCategory): boolean {
    return this.selectedSubActivities.some(s => s.id === activity.id && !s.removedPending) || this.temporarySelectedActivityIds.has(activity.id);
  }

  toggleActivity(activity: CharityCategory) {
    const idx = this.selectedSubActivities.findIndex(s => s.id === activity.id);
    if (idx > -1 && !this.selectedSubActivities[idx].isTemp) {
      this.selectedSubActivities[idx].removedPending = !this.selectedSubActivities[idx].removedPending;
      if (this.showOnlySelectedActivities) this.filterActivities();
      return;
    }

    const persistedIndex = this.selectedSubActivities.findIndex(s => s.id === activity.id && !s.isTemp && s.subscriptionItemId);
    if (persistedIndex > -1) {
      this.selectedSubActivities.splice(persistedIndex, 1);
      if (this.showOnlySelectedActivities) this.filterActivities();
      this.temporarySelectedActivityIds.delete(activity.id);
      return;
    }

    if (this.temporarySelectedActivityIds.has(activity.id)) {
      this.temporarySelectedActivityIds.delete(activity.id);
      const tempIdx = this.selectedSubActivities.findIndex(s => s.id === activity.id && s.isTemp);
      if (tempIdx > -1) this.selectedSubActivities.splice(tempIdx, 1);
      if (this.showOnlySelectedActivities) this.filterActivities();
    } else {
      this.temporarySelectedActivityIds.add(activity.id);
      if (!this.selectedSubActivities.some(s => s.id === activity.id))
        this.selectedSubActivities.push({ id: activity.id, category: activity.parent, activity: activity.child, isTemp: true });
      if (this.showOnlySelectedActivities) this.filterActivities();
    }
  }

  isRegionSelected(activity: RegionVm): boolean {
    return this.selectedRegions.some(s => s.id === activity.id && !s.removedPending) || this.temporarySelectedRegionIds.has(activity.id);
  }

  toggleRegion(activity: RegionVm) {
    const idx = this.selectedRegions.findIndex(s => s.id === activity.id);
    if (idx > -1 && !this.selectedRegions[idx].isTemp) {
      this.selectedRegions[idx].removedPending = !this.selectedRegions[idx].removedPending;
      if (this.showOnlySelectedRegions) this.filterRegions();
      return;
    }

    const persistedIndex = this.selectedRegions.findIndex(s => s.id === activity.id && !s.isTemp && s.subscriptionItemId);
    if (persistedIndex > -1) {
      this.selectedRegions.splice(persistedIndex, 1);
      if (this.showOnlySelectedRegions) this.filterRegions();
      this.temporarySelectedRegionIds.delete(activity.id);
      return;
    }

    if (this.temporarySelectedRegionIds.has(activity.id)) {
      this.temporarySelectedRegionIds.delete(activity.id);
      const tempIdx = this.selectedRegions.findIndex(s => s.id === activity.id && s.isTemp);
      if (tempIdx > -1) this.selectedRegions.splice(tempIdx, 1);
      if (this.showOnlySelectedRegions) this.filterRegions();
    } else {
      this.temporarySelectedRegionIds.add(activity.id);
      if (!this.selectedRegions.some(s => s.id === activity.id))
        this.selectedRegions.push({ id: activity.id, code: activity.code, name: activity.name, isTemp: true });
      if (this.showOnlySelectedRegions) this.filterRegions();
    }
  }

  selectAllVisibleActivities() {
    (this.filteredActivitiesList ?? []).forEach(a => {
      if (!this.selectedSubActivities.some(s => s.id === a.id))
        this.selectedSubActivities.push({ id: a.id, category: a.parent, activity: a.child, isTemp: true });
      this.temporarySelectedActivityIds.add(a.id);
    });
  }

  clearAllVisibleActivities() {
    const visibleIds = new Set((this.filteredActivitiesList ?? []).map(a => a.id));
    visibleIds.forEach(id => {
      this.temporarySelectedActivityIds.delete(id);
      const tempIdx = this.selectedSubActivities.findIndex(s => s.id === id && s.isTemp);
      if (tempIdx > -1) this.selectedSubActivities.splice(tempIdx, 1);
      const persistedIdx = this.selectedSubActivities.findIndex(s => s.id === id && !s.isTemp && s.subscriptionItemId);
      if (persistedIdx > -1) this.selectedSubActivities[persistedIdx].removedPending = true;
    });
  }

  selectAllVisibleRegions() {
    (this.filteredRegionList ?? []).forEach(r => {
      if (!this.selectedRegions.some(s => s.id === r.id))
        this.selectedRegions.push({ id: r.id, code: r.code, name: r.name, isTemp: true });
      this.temporarySelectedRegionIds.add(r.id);
    });
  }

  clearAllVisibleRegions() {
    const visibleIds = new Set((this.filteredRegionList ?? []).map(r => r.id));
    visibleIds.forEach(id => {
      this.temporarySelectedRegionIds.delete(id);
      const tempIdx = this.selectedRegions.findIndex(s => s.id === id && s.isTemp);
      if (tempIdx > -1) this.selectedRegions.splice(tempIdx, 1);
      const persistedIdx = this.selectedRegions.findIndex(s => s.id === id && !s.isTemp && s.subscriptionItemId);
      if (persistedIdx > -1) this.selectedRegions[persistedIdx].removedPending = true;
    });
  }

  onBeneficiarySearch() {
    if (this.showOnlySelectedBeneficiaries) {
      const items = this.data?.items ?? [];
      this.beneficiarySearchResults = (items || []).filter((it: any) => it.id).map((r: any) => ({ ...r }));

      this.beneficiarySearchResults.forEach(r => {
        if (!Array.isArray(r.aliases)) {
          if (typeof r.aliases === 'string' && r.aliases.trim().length > 0) r.aliases = r.aliases.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0);
          else r.aliases = [];
        }
        if (!Array.isArray(r.previousNames)) {
          if (typeof r.previousNames === 'string' && r.previousNames.trim().length > 0) r.previousNames = r.previousNames.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0);
          else r.previousNames = [];
        }
      });

      this.tooltipVisible = {};
      this.tooltipContent = {};
      this.tooltipPosition = {};
      this.tooltipHideTimeouts = {};
      this.isMouseOverTooltip = {};
      return;
    }

    const params = new HttpParams()
      .set('beneficiaryname', this.beneficiarySearchQuery.beneficiaryName || '')
      .set('beneficiaryaliasname', this.beneficiarySearchQuery.beneficiaryName || '')
      .set('beneficiaryurn', this.beneficiarySearchQuery.beneficiaryURN || '')
      .set('address', this.beneficiarySearchQuery.address || '')
      .set('postcode', this.beneficiarySearchQuery.postcode || '');

    this.http.get<any[]>('/charitycentral/getbeneficiaries', { params })
      .subscribe(results => {
        this.beneficiarySearchResults = results || [];
        this.beneficiarySearchResults.forEach(r => {
          if (!Array.isArray(r.aliases)) {
            if (typeof r.aliases === 'string' && r.aliases.trim().length > 0) r.aliases = r.aliases.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0);
            else r.aliases = [];
          }
          if (!Array.isArray(r.previousNames)) {
            if (typeof r.previousNames === 'string' && r.previousNames.trim().length > 0) r.previousNames = r.previousNames.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0);
            else r.previousNames = [];
          }
        });

        this.tooltipVisible = {};
        this.tooltipContent = {};
        this.tooltipPosition = {};
        this.tooltipHideTimeouts = {};
        this.isMouseOverTooltip = {};
      });
  }

  toggleAddBeneficiaryForm() {
    this.showAddBeneficiaryForm = !this.showAddBeneficiaryForm;
  }

  saveNewBeneficiary() {
    this.selectedBeneficiary = {
      id: Math.floor(Math.random() * 100000),
      name: this.newBeneficiary.name,
      beneficiarySourceURN: this.newBeneficiary.beneficiarySourceURN,
      addressLine1: this.newBeneficiary.addressLine1,
      addressLine2: this.newBeneficiary.addressLine2,
      postcode: this.newBeneficiary.postcode,
      email: this.newBeneficiary.email
    };
    this.alert = { text: 'Beneficiary added successfully', type: 'success' };
    this.showAddBeneficiaryForm = false;
  }

  getCharityCategoryList() {
    this.http.get<CharityCategory[]>('/charitycentral/getcharitycategories')
      .subscribe(results => {
        this.allCharityCategories = results;
        this.filteredActivitiesList = [...this.allCharityCategories];
        this.prepopulateCategoriesFromData();
        if (this.openModeOnInit === 'category') {
          this.onDiscretionaryLookup();
          this.openModeOnInit = null;
        }
      });
  }

  getRegions() {
    const params = new HttpParams().set('user', 'system');
    this.http.get<any>('/charitycentral/getregionidswithcode', { params }).subscribe({
      next: (res) => {
        let autoId = 1;
        this.regions = Object.keys(res).map(code => ({ id: autoId++, code: code, name: res[code] }));
        this.filteredRegionList = [...this.regions];
        this.prepopulateRegionsFromData();
        if (this.openModeOnInit === 'region') {
          this.onDiscretionaryRegionLookup();
          this.openModeOnInit = null;
        }
      },
      error: err => console.error('Region load failed', err)
    });
  }

  canSave() {
    if (this.serviceTypeId === 1) return this.selectedBeneficiaryIds && this.selectedBeneficiaryIds.size > 0;
    return (this.selectedSubActivities && this.selectedSubActivities.length > 0) ||
      (this.selectedRegions && this.selectedRegions.length > 0) ||
      (this.temporarySelectedActivityIds && this.temporarySelectedActivityIds.size > 0) ||
      (this.temporarySelectedRegionIds && this.temporarySelectedRegionIds.size > 0);
  }

  save() {
    this.isSaving = true;
    this.mergeTemporarySelections();

    let params = new HttpParams().set('subscriptionId', this.subscriptionId.toString()).set('user', this.loggedInUserTag || 'System');

    // ServiceType1 (named beneficiaries) has extra handling for adds/removes
    if (this.serviceTypeId === 1) {
      const persistedIds = Array.from(this.persistedBeneficiaryIds || new Set<number>());
      const selectedIds = Array.from(this.selectedBeneficiaryIds || new Set<number>());
      const toDeleteIds = persistedIds.filter(id => selectedIds.indexOf(id) === -1);
      const toAddIds = selectedIds.filter(id => persistedIds.indexOf(id) === -1);

      // Remove a beneficiary by its id — this clears all of its SubscriptionItem rows
      // (including any per-region rows), so it can't reappear on refresh.
      const deleteCalls: any[] = [];
      toDeleteIds.forEach(id => {
        const p = new HttpParams()
          .set('subscriptionId', this.subscriptionId.toString())
          .set('beneficiaryId', id.toString())
          .set('user', this.loggedInUserTag || 'System');
        deleteCalls.push(this.http.post<boolean>('/charitycentral/deletebeneficiaryfromsubscription', null, { params: p }));
      });

      const runCreates = () => {
        if (toAddIds.length === 0) {
          this.isSaving = false;
          this.dialogRef.close({ selectedIds: selectedIds });
          return;
        }

        let createParams = new HttpParams().set('subscriptionId', this.subscriptionId.toString()).set('user', this.loggedInUserTag || 'System');
        toAddIds.forEach(id => (createParams = createParams.append('beneficiaryId', id.toString())));

        this.http.post<boolean>('/charitycentral/createsubscriptionitem', null, { params: createParams }).subscribe({
          next: () => {
            this.isSaving = false;
            this.dialogRef.close({ selectedIds: selectedIds });
          },
          error: err => {
            this.isSaving = false;
            console.error('Error creating subscription items', err);
            this.alert = { text: 'An error occurred while saving subscription beneficiaries.', type: 'error' };
          }
        });
      };

      if (deleteCalls.length > 0) {
        forkJoin(deleteCalls).subscribe({
          next: () => runCreates(),
          error: err => {
            this.isSaving = false;
            console.error('Error deleting old subscription items', err);
            this.alert = { text: 'An error occurred while removing unselected beneficiaries.', type: 'error' };
          }
        });
      } else {
        runCreates();
      }

      return;
    }

    // For other service types (categories/regions) append beneficiary ids if any
    if (this.selectedBeneficiaryIds && this.selectedBeneficiaryIds.size > 0) {
      this.selectedBeneficiaryIds.forEach((r: number) => {
        params = params.append('beneficiaryId', r.toString());
      });
    }

    this.http.post<boolean>('/charitycentral/createsubscriptionitem', null, { params }).subscribe({
      next: (res) => {
        this.isSaving = false;
        if (res === true || res === undefined || res === null) this.dialogRef.close(true);
        else this.alert = { text: 'Saving subscription items failed (server returned false).', type: 'error' };
      },
      error: err => {
        this.isSaving = false;
        console.error('Adding subscription items failed', err);
        this.alert = { text: 'An error occurred while saving subscription items.', type: 'error' };
      }
    });
  }

  private saveNewSelections(newCategoryIds: number[], newRegionIds: number[]) {
    if ((newCategoryIds == null || newCategoryIds.length === 0) && (newRegionIds == null || newRegionIds.length === 0)) {
      this.dialogRef.close(true);
      return;
    }

    let params = new HttpParams().set('subscriptionId', this.subscriptionId.toString()).set('user', this.loggedInUserTag || 'System');
    (newCategoryIds || []).forEach(id => params = params.append('charityCategoryId', id.toString()));
    (newRegionIds || []).forEach(id => params = params.append('regionIds', id.toString()));

    this.isSaving = true;
    this.http.post<boolean>('/charitycentral/createsubscriptionitem', null, { params }).subscribe({
      next: (res) => {
        this.isSaving = false;
        if (res === true || res === undefined || res === null) this.dialogRef.close(true);
        else this.alert = { text: 'Saving subscription items failed (server returned false).', type: 'error' };
      },
      error: err => {
        this.isSaving = false;
        console.error('Error creating subscription items', err);
        this.alert = { text: 'An error occurred while saving selected items.', type: 'error' };
      }
    });
  }

  mergeTemporarySelections() {
    if (this.temporarySelectedActivityIds && this.temporarySelectedActivityIds.size > 0) {
      this.temporarySelectedActivityIds.forEach(id => {
        if (!this.selectedSubActivities.some(s => s.id === id)) {
          const cat = this.allCharityCategories.find(a => a.id === id);
          if (cat) this.selectedSubActivities.push({ id: cat.id, category: cat.parent, activity: cat.child, isTemp: true });
        }
      });
    }

    if (this.temporarySelectedRegionIds && this.temporarySelectedRegionIds.size > 0) {
      this.temporarySelectedRegionIds.forEach(id => {
        if (!this.selectedRegions.some(s => s.id === id)) {
          const reg = this.regions.find(r => r.id === id);
          if (reg) this.selectedRegions.push({ id: reg.id, name: reg.name, code: reg.code, isTemp: true });
        }
      });
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  onBeneficiarySearchKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onBeneficiarySearch();
    }
  }

  onDiscretionaryLookup() {
    this.selectedCategory = '';
    this.filteredSubActivities = [];
    this.activitySearchTerm = '';
    this.filteredActivitiesList = [...this.allCharityCategories];
    this.temporarySelectedActivityIds.clear();
    this.isDiscretionaryCategoryPopupOpen = true;
  }

  onDiscretionaryRegionLookup() {
    this.isDiscretionaryRegionPopupOpen = true;
    this.selectedCategory = '';
    this.filteredSubActivities = [];
    this.activitySearchTerm = '';
    this.filteredRegionList = [...this.regions];
    this.temporarySelectedRegionIds.clear();
  }

  closeDiscretionaryCategoryPopup() {
    this.isCategoryDropdownOpen = false;
    this.isDiscretionaryCategoryPopupOpen = false;
    this.closeCategoryPopup();
    this.cancel();
  }

  closeDiscretionaryRegionPopup() {
    this.isRegionDropdownOpen = false;
    this.isDiscretionaryRegionPopupOpen = false;
    this.closeRegionPopup();
    this.cancel();
  }

  closeBeneficiaryLookup() {
    this.isBeneficiaryPopupOpen = false;
    this.cancel();
  }

  closeRegionPopup() {
    this.isDiscretionaryRegionPopupOpen = false;
    this.temporarySelectedRegionIds.clear();
    this.filteredRegions = [];
    this.regionSearchTerm = '';
    this.filteredRegionList = [];
  }

  closeCategoryPopup() {
    this.isDiscretionaryCategoryPopupOpen = false;
    this.temporarySelectedActivityIds.clear();
    this.filteredSubActivities = [];
    this.activitySearchTerm = '';
    this.filteredActivitiesList = [];
  }

  applyDiscretionaryCategories() {
    const newCategoryIds = this.selectedSubActivities.filter(s => !s.subscriptionItemId && !s.removedPending).map(s => s.id);
    const removedSubscriptionItemIds = this.selectedSubActivities.filter(s => s.subscriptionItemId && s.removedPending).map(s => s.subscriptionItemId as number);

    this.temporarySelectedActivityIds.clear();
    this.isCategoryDropdownOpen = false;
    this.isDiscretionaryCategoryPopupOpen = false;

    if (removedSubscriptionItemIds.length > 0) {
      const deletes = removedSubscriptionItemIds.map(id => {
        const p = new HttpParams().set('subscriptionItemId', id.toString()).set('user', this.loggedInUserTag || 'System');
        return this.http.post<boolean>('/charitycentral/deletesubscriptionitem', null, { params: p });
      });

      forkJoin(deletes).subscribe({
        next: () => this.saveNewSelections(newCategoryIds, []),
        error: err => {
          console.error('Error deleting subscription items', err);
          this.alert = { text: 'An error occurred while removing selected categories.', type: 'error' };
        }
      });
    } else {
      this.saveNewSelections(newCategoryIds, []);
    }
  }

  applyDiscretionaryRegions() {
    const newRegionIds = this.selectedRegions.filter(r => !r.subscriptionItemId && !r.removedPending).map(r => r.id);
    const removedSubscriptionItemIds = this.selectedRegions.filter(r => r.subscriptionItemId && r.removedPending).map(r => r.subscriptionItemId as number);

    this.temporarySelectedRegionIds.clear();
    this.isRegionDropdownOpen = false;
    this.isDiscretionaryRegionPopupOpen = false;

    if (removedSubscriptionItemIds.length > 0) {
      const deletes = removedSubscriptionItemIds.map(id => {
        const p = new HttpParams().set('subscriptionItemId', id.toString()).set('user', this.loggedInUserTag || 'System');
        return this.http.post<boolean>('/charitycentral/deletesubscriptionitem', null, { params: p });
      });

      forkJoin(deletes).subscribe({
        next: () => this.saveNewSelections([], newRegionIds),
        error: err => {
          console.error('Error deleting subscription items', err);
          this.alert = { text: 'An error occurred while removing selected regions.', type: 'error' };
        }
      });
    } else {
      this.saveNewSelections([], newRegionIds);
    }
  }

  removeSelectedActivity(item: SelectedDiscretionaryCategory) {
    const index = this.selectedSubActivities.findIndex(s => s.id === item.id);
    if (index > -1) {
      if (item.subscriptionItemId) {
        this.selectedSubActivities[index].removedPending = true;
        if (this.showOnlySelectedActivities) this.filterActivities();
      } else {
        this.selectedSubActivities.splice(index, 1);
        this.temporarySelectedActivityIds.delete(item.id);
        if (this.showOnlySelectedActivities) this.filterActivities();
      }
    }
  }

  removeSelectedRegions(item: RegionVm) {
    const index = this.selectedRegions.findIndex(s => s.id === item.id);
    if (index > -1) {
      if (item.subscriptionItemId) {
        this.selectedRegions[index].removedPending = true;
        if (this.showOnlySelectedRegions) this.filterRegions();
      } else {
        this.selectedRegions.splice(index, 1);
        this.temporarySelectedRegionIds.delete(item.id);
        if (this.showOnlySelectedRegions) this.filterRegions();
      }
    }
  }

  handleOverlayMouseDown(event: MouseEvent, overlayClass: string, closeCallback: () => void): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains(overlayClass)) closeCallback();
  }

  onDiscretionaryCategoryOverlayMouseDown(event: MouseEvent) {
    this.handleOverlayMouseDown(event, 'category-popup-overlay', () => this.closeDiscretionaryCategoryPopup());
  }

  onBeneficiaryOverlayMouseDown(event: MouseEvent) {
    this.handleOverlayMouseDown(event, 'category-popup-overlay', () => this.closeBeneficiaryLookup());
  }

  private prepopulateFromParentItems() {
    const items = this.data?.items ?? [];
    if (!items || items.length === 0) return;

    items.forEach((it: any) => {
      if (it.charityCategoryId) {
        const cat = this.allCharityCategories.find(a => a.id === it.charityCategoryId);
        if (cat) {
          if (!this.selectedSubActivities.some(s => s.id === cat.id))
            this.selectedSubActivities.push({ id: cat.id, category: cat.parent, activity: cat.child, subscriptionItemId: it.id });
        } else {
          if (!this.selectedSubActivities.some(s => s.id === it.charityCategoryId))
            this.selectedSubActivities.push({ id: it.charityCategoryId, category: 'Unknown', activity: 'Unknown', subscriptionItemId: it.id });
        }
      }

      if (it.regionId) {
        const reg = this.regions.find(r => r.id === it.regionId);
        if (reg) {
          if (!this.selectedRegions.some(s => s.id === reg.id))
            this.selectedRegions.push({ id: reg.id, code: reg.code, name: reg.name, subscriptionItemId: it.id });
        } else {
          if (!this.selectedRegions.some(s => s.id === it.regionId))
            this.selectedRegions.push({ id: it.regionId, code: '', name: 'Unknown', subscriptionItemId: it.id });
        }
      }

      // Track the persisted beneficiary by its real Beneficiary.Id (carried only on Named items).
      // Category/region items (Discretionary/Trust) have no beneficiaryId, so they are skipped
      // and never leak into the beneficiary selection.
      if (it.beneficiaryId != null) {
        const bid = Number(it.beneficiaryId);
        if (!Number.isNaN(bid)) {
          this.selectedBeneficiaryIds.add(bid);
          this.persistedBeneficiaryIds.add(bid);
        }
      }
    });
  }

  private prepopulateCategoriesFromData() {
    const items = this.data?.items ?? [];
    if (!items || items.length === 0) return;
    items.forEach((it: any) => {
      if (it.charityCategoryId) {
        const cat = this.allCharityCategories.find(a => a.id === it.charityCategoryId);
        if (cat) {
          const existingIdx = this.selectedSubActivities.findIndex(s => s.id === cat.id);
          if (existingIdx === -1) this.selectedSubActivities.push({ id: cat.id, category: cat.parent, activity: cat.child, subscriptionItemId: it.id });
          else {
            this.selectedSubActivities[existingIdx].category = cat.parent;
            this.selectedSubActivities[existingIdx].activity = cat.child;
            this.selectedSubActivities[existingIdx].subscriptionItemId = it.id;
          }
        }
      }
    });
  }

  private prepopulateRegionsFromData() {
    const items = this.data?.items ?? [];
    if (!items || items.length === 0) return;
    items.forEach((it: any) => {
      if (it.regionId) {
        const reg = this.regions.find(r => r.id === it.regionId);
        if (reg) {
          const existingIdx = this.selectedRegions.findIndex(s => s.id === reg.id);
          if (existingIdx === -1) this.selectedRegions.push({ id: reg.id, code: reg.code, name: reg.name, subscriptionItemId: it.id });
          else {
            this.selectedRegions[existingIdx].code = reg.code;
            this.selectedRegions[existingIdx].name = reg.name;
            this.selectedRegions[existingIdx].subscriptionItemId = it.id;
          }
        }
      }
    });
  }

  showTooltip(index: number, aliases: string[], previousNames: string[], cell: HTMLElement) {
    if (this.tooltipHideTimeouts[index]) {
      clearTimeout(this.tooltipHideTimeouts[index]);
      this.tooltipHideTimeouts[index] = null;
    }

    this.tooltipVisible[index] = true;
    this.tooltipContent[index] = { aliases: aliases || [], previousNames: previousNames || [] };

    const rect = cell.getBoundingClientRect();
    const offset = 8;
    const tooltipWidth = 420;
    const maxItems = Math.max(aliases?.length || 0, previousNames?.length || 0);
    const tooltipHeight = Math.min(maxItems * 28 + 80, 400);

    let top = rect.bottom + offset;
    if (top + tooltipHeight > window.innerHeight - 20) top = rect.top - tooltipHeight - offset;

    let left = rect.left;
    const maxLeft = window.innerWidth - tooltipWidth - 20;
    if (left > maxLeft) left = maxLeft;
    if (left < 8) left = 8;

    this.tooltipPosition[index] = { top, left };
  }

  hideTooltip(index: number) {
    this.tooltipHideTimeouts[index] = setTimeout(() => {
      if (!this.isMouseOverTooltip[index]) this.tooltipVisible[index] = false;
    }, 150);
  }

  onTooltipMouseEnter(index: number) {
    this.isMouseOverTooltip[index] = true;
    if (this.tooltipHideTimeouts[index]) {
      clearTimeout(this.tooltipHideTimeouts[index]);
      this.tooltipHideTimeouts[index] = null;
    }
  }

  onTooltipMouseLeave(index: number) {
    this.isMouseOverTooltip[index] = false;
    this.tooltipVisible[index] = false;
  }

  toggleShowOnlySelectedActivities() {
    this.showOnlySelectedActivities = !this.showOnlySelectedActivities;
    this.filterActivities();
  }

  toggleShowOnlySelectedRegions() {
    this.showOnlySelectedRegions = !this.showOnlySelectedRegions;
    this.filterRegions();
  }

  toggleShowOnlySelectedBeneficiaries() {
    this.showOnlySelectedBeneficiaries = !this.showOnlySelectedBeneficiaries;
    if (this.showOnlySelectedBeneficiaries) {
      const items = this.data?.items ?? [];
      this.beneficiarySearchResults = (items || []).filter((it: any) => it.id).map((r: any) => ({ ...r }));
      this.beneficiarySearchResults.forEach(r => {
        // Key on the real beneficiary id (never the SubscriptionItem row id), otherwise
        // SI.Ids leak into the selection set and get inserted as bogus beneficiaries on Save.
        const id = this.beneficiaryKey(r);
        if (id !== undefined && !Number.isNaN(id)) this.selectedBeneficiaryIds.add(id);
      });
    } else {
      this.beneficiarySearchResults = [];
    }
  }
}
